import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  usePredefinedOptionListPreview,
  usePredefinedOptionLists,
} from "../hooks/usePredefinedOptionLists";
import { sortEntriesInactiveLast } from "../types/predefinedOptionList";

type Props = {
  /** Current list code; null/empty = typed options. */
  code: string | null | undefined;
  onChangeCode: (code: string | null) => void;
  /** Typed options that will be removed when switching to a list. */
  typedOptionCount: number;
  /** "kpi" for KPI options, "column" for a grid column. */
  target?: "kpi" | "column";
  /** Unique id prefix (several column editors can be on screen). */
  idPrefix: string;
};

export function optionSourceSwitchMessage(count: number, target: "kpi" | "column"): string {
  const options = `${count} typed option${count === 1 ? "" : "s"}`;
  const where = target === "column" ? " for this column" : "";
  return `Switching to a list removes the ${options}${where}. Answers already saved keep their values.`;
}

const KpiOptionSourceEditor: React.FC<Props> = ({
  code,
  onChangeCode,
  typedOptionCount,
  target = "kpi",
  idPrefix,
}) => {
  const usesList = !!code;
  const { lists, loading: listsLoading } = usePredefinedOptionLists();
  const { preview, loading: previewLoading, failed } = usePredefinedOptionListPreview(code);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const selectedList = lists.find((l) => l.predefined_option_list_code === code) ?? null;
  const firstCode = lists[0]?.predefined_option_list_code ?? null;

  const chooseList = () => {
    if (usesList || !firstCode) return;
    if (typedOptionCount > 0) {
      setPendingCode(firstCode);
      return;
    }
    onChangeCode(firstCode);
  };

  const confirmSwitch = () => {
    if (pendingCode) onChangeCode(pendingCode);
    setPendingCode(null);
  };

  const entries = sortEntriesInactiveLast(preview?.entries ?? []);

  return (
    <div className="mb-3" data-testid={`${idPrefix}-option-source`}>
      <Form.Label className="d-block">Option source</Form.Label>
      <div className="d-flex flex-wrap gap-3">
        <Form.Check
          type="radio"
          id={`${idPrefix}-source-typed`}
          name={`${idPrefix}-source`}
          label="Type options"
          checked={!usesList}
          onChange={() => onChangeCode(null)}
        />
        <Form.Check
          type="radio"
          id={`${idPrefix}-source-list`}
          name={`${idPrefix}-source`}
          label="Use a list"
          checked={usesList}
          disabled={!usesList && (listsLoading || !firstCode)}
          onChange={chooseList}
        />
      </div>

      {usesList ? (
        <div className="mt-2">
          <Form.Group controlId={`${idPrefix}-list`} style={{ maxWidth: 360 }}>
            <Form.Label>List</Form.Label>
            <Form.Select
              value={code ?? ""}
              onChange={(e) => onChangeCode(e.target.value || null)}
              data-testid={`${idPrefix}-list-select`}
            >
              {selectedList === null && code ? <option value={code}>{code}</option> : null}
              {lists.map((list) => (
                <option
                  key={list.predefined_option_list_code}
                  value={list.predefined_option_list_code}
                  title={list.level_hint}
                >
                  {list.name}
                </option>
              ))}
            </Form.Select>
            {selectedList ? (
              <Form.Text muted className="d-block">
                {selectedList.description ? `${selectedList.description} ` : ""}
                {selectedList.level_hint}
              </Form.Text>
            ) : null}
          </Form.Group>

          <div className="border rounded p-2 mt-2 bg-light" data-testid={`${idPrefix}-list-preview`}>
            <div className="small text-muted mb-1">
              {preview?.context_name
                ? `Preview (read-only, from ${preview.context_name})`
                : "Preview (read-only)"}
            </div>
            {previewLoading ? (
              <Spinner animation="border" size="sm" />
            ) : failed ? (
              <div className="small text-muted">The preview couldn't be loaded.</div>
            ) : entries.length === 0 ? (
              <div className="small text-muted">This list has no entries yet.</div>
            ) : (
              <ul className="list-unstyled mb-0 small" style={{ maxHeight: 180, overflowY: "auto" }}>
                {entries.map((entry) => (
                  <li key={entry.id} className={entry.is_active ? undefined : "text-muted"}>
                    {entry.name}
                    {entry.is_active ? "" : " (inactive)"}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Form.Text muted className="d-block">
            Entries come from the list. &quot;Other&quot; isn&apos;t available with a list.
          </Form.Text>
        </div>
      ) : null}

      <Modal show={pendingCode !== null} onHide={() => setPendingCode(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6">Use a list?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{optionSourceSwitchMessage(typedOptionCount, target)}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPendingCode(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSwitch} data-testid={`${idPrefix}-source-confirm`}>
            Use a list
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KpiOptionSourceEditor;
